import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import {
  AppLang,
  AVAILABLE_LANGS,
  DEFAULT_LANG,
  detectPreferredLang,
  isAppLang,
  persistLang,
} from './languages';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private titleSub?: Subscription;
  private lastTitleKey: string | null = null;

  readonly langs = AVAILABLE_LANGS;

  currentLang(): AppLang {
    const urlLang = this.router.url.split(/[/?#]/).filter(Boolean)[0];
    if (isAppLang(urlLang)) {
      return urlLang;
    }
    const fromRoute = this.route.snapshot.firstChild?.paramMap.get('lang')
      ?? this.route.snapshot.paramMap.get('lang');
    if (isAppLang(fromRoute)) {
      return fromRoute;
    }
    const active = this.transloco.getActiveLang();
    return isAppLang(active) ? active : DEFAULT_LANG;
  }

  link(commands: (string | number)[]): (string | number)[] {
    return ['/', this.currentLang(), ...commands];
  }

  applyLang(lang: AppLang): void {
    persistLang(lang);
    this.transloco.setActiveLang(lang);
    document.documentElement.lang = lang;
    this.transloco.load(lang).subscribe(() => {
      this.meta.updateTag({
        name: 'description',
        content: this.transloco.translate('meta.description'),
      });
      if (this.lastTitleKey) {
        this.applyResolvedTitle(this.lastTitleKey);
      }
    });
  }

  switchLang(lang: AppLang): void {
    if (!isAppLang(lang) || lang === this.currentLang()) {
      this.applyLang(lang);
      return;
    }

    const url = this.router.url.split('?')[0];
    const segments = url.split('/').filter(Boolean);
    if (segments.length && isAppLang(segments[0])) {
      segments[0] = lang;
    } else {
      segments.unshift(lang);
    }
    this.applyLang(lang);
    void this.router.navigateByUrl('/' + segments.join('/'));
  }

  syncFromUrl(lang: string | null): AppLang {
    const resolved = isAppLang(lang) ? lang : detectPreferredLang();
    this.applyLang(resolved);
    return resolved;
  }

  setPageTitle(pageKey: string | null | undefined): void {
    const key = pageKey ? `titles.${pageKey}` : 'titles.home';
    this.lastTitleKey = key;
    this.titleSub?.unsubscribe();
    const lang = this.currentLang();
    this.titleSub = this.transloco
      .load(lang)
      .pipe(
        switchMap(() => this.transloco.selectTranslate(key)),
        take(1)
      )
      .subscribe((translated) => {
        this.applyResolvedTitle(key, translated);
      });
  }

  private applyResolvedTitle(key: string, translated?: string): void {
    const value = translated ?? this.transloco.translate(key);
    if (!value || value === key || value.startsWith('titles.')) {
      this.title.setTitle('Amvara');
      return;
    }
    // Home key already includes branding ("Welcome to Amvara")
    if (key === 'titles.home') {
      this.title.setTitle(value);
      return;
    }
    this.title.setTitle(`Amvara - ${value}`);
  }

  /**
   * Fallback for leftover /#/… bookmarks. Prefer the early script in index.html.
   * Rewrites hash → path only; PrefixedLangRedirectGuard adds lang (avoids /en/en/…).
   */
  redirectLegacyHash(): boolean {
    const hash = window.location.hash || '';
    if (!hash.startsWith('#/')) {
      return false;
    }
    const path = hash.slice(1) || '/';
    const search = window.location.search || '';
    const next = path + search;
    const current = window.location.pathname + search;
    if (next !== current) {
      window.location.replace(next);
    } else {
      window.history.replaceState(null, '', next);
    }
    return true;
  }
}
